import { useDebounce, useDepositData, useExtractUserName } from '@/shared/lib';
import { COMMISSION_CHECK_DELAY } from '@/shared/config';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useGetSccrsGamingHalls } from '@/entities/sccrs';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import type { SccrsFormValues } from '@/entities/sccrs';

interface UseDepositSccrsFormLogicOptions {
  initialData?: Partial<SccrsFormValues>;
  isMinAmount?: boolean;
  isMaxAmount?: boolean;
  currentMinAmount?: number;
  currentMaxAmount?: number;
  maxAvailable?: number;
}

export const useSccrsFormLogic = ({
  initialData,
  isMinAmount = false,
  isMaxAmount = false,
  currentMinAmount = 0,
  currentMaxAmount,
  maxAvailable = 0,
}: UseDepositSccrsFormLogicOptions) => {
  const { t } = useTranslation();
  const [, setFormData] = useState<Partial<SccrsFormValues> | null>(
    initialData || null
  );

  const { accountNumber, depositWithdrawAssetsData, assetsIconsData } =
    useDepositData();

  const { isGamingHallsLoading, gamingHalls, gamingHallsError } =
    useGetSccrsGamingHalls({
      halltype: 'physical',
    });

  const { userFullName, isUserProfileInfoLoading, userProfileInfoError } =
    useExtractUserName();

  const [currentCurrencyCode, setCurrentCurrencyCode] = useState('');

  const sccrsForm = useForm<SccrsFormValues>({
    mode: 'onChange',
    resolver: zodResolver(
      z.object({
        currencyCode: z
          .string()
          .nonempty({ message: t('authPages.validation.requiredField') }),
        fls: z
          .string()
          .nonempty({ message: t('authPages.validation.requiredField') }),
        platform: z
          .string()
          .nonempty({ message: t('authPages.validation.requiredField') }),
        amount: z
          .string()
          .refine(val => Number(val) > 0, {
            message: t('authPages.validation.requiredField'),
          })
          .refine(val => !isMinAmount || Number(val) >= currentMinAmount, {
            message: t('depositMethods.sccrs.validation.minAmount', {
              amount: currentMinAmount,
              currency: currentCurrencyCode || '',
            }),
          })
          .superRefine((val, ctx) => {
            if (currentMaxAmount && Number(val) > currentMaxAmount) {
              ctx.addIssue({
                code: 'too_big',
                origin: 'number',
                maximum: currentMaxAmount,
                inclusive: true,
                message: t('common.maxAmount', {
                  amount: currentMaxAmount,
                  currency: currentCurrencyCode || '',
                }),
              });
            }
          })
          .refine(val => !isMaxAmount || Number(val) <= maxAvailable, {
            message: t('', {
              amount: maxAvailable,
              currency: currentCurrencyCode || '',
            }),
          }),
      })
    ),
    defaultValues: {
      currencyCode: initialData?.currencyCode || 'BYN',
      fls: initialData?.fls || userFullName,
      platform: initialData?.platform || '',
      amount: String(initialData?.amount),
      includeFee: initialData?.includeFee || false,
    },
  });

  const watchedValues = sccrsForm.watch();

  const platformOptions = useMemo(() => {
    if (!gamingHalls || gamingHalls.gamingHalls?.length === 0) return [];

    return gamingHalls.gamingHalls;
  }, [gamingHalls?.gamingHalls, isGamingHallsLoading]);

  useEffect(() => {
    setCurrentCurrencyCode(watchedValues.currencyCode);
  }, [watchedValues.currencyCode]);

  // Commission logic (using shared logic for basic functionality)
  const debouncedAmount = useDebounce(
    watchedValues.amount,
    COMMISSION_CHECK_DELAY
  );

  const normalizedAmount = watchedValues.amount ?? NaN;

  const hasCommissionInputs = Boolean(
    watchedValues.currencyCode &&
      !Number.isNaN(normalizedAmount) &&
      Number(normalizedAmount) > 0
  );

  const isCommissionEnabled = !!(
    watchedValues.currencyCode &&
    !Number.isNaN(debouncedAmount) &&
    Number(debouncedAmount) > 0
  );

  return {
    sccrsForm,
    watchedValues,

    setFormData,
    accountNumber,

    // Data
    platformOptions,
    depositWithdrawAssetsData,
    assetsIconsData,
    currentCurrencyCode,

    isCommissionEnabled,
    hasCommissionInputs,
    debouncedAmount,
    normalizedAmount,
    userProfileInfoError,
    gamingHallsError,

    // Loading states
    isUserProfileInfoLoading,
    isGamingHallsLoading,
  };
};
